import { Component, inject, input, signal } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, delay, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { FeedbackFailComponent, FeedbackSuccessComponent, FormFieldLoadingComponent } from 'ok-angular-shared/components';
import { G4Service, GetG4BusinessAccountResponse } from '../../services/g4.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'adm-g4-business-account-number',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FeedbackSuccessComponent,
    FeedbackFailComponent,
    FormFieldLoadingComponent,
  ],
  templateUrl: './adm-g4-business-account-number.component.html',
  styleUrl: './adm-g4-business-account-number.component.scss',
})
export class AdmG4BusinessAccountNumberComponent {
  service = inject(G4Service);
  accountDetails = signal<GetG4BusinessAccountResponse | null>(null);
  control = input.required<FormControl<number | null>>();
  private patternRegex = /^\d{5,8}$/;

  constructor() {
    toObservable(this.control)
      .pipe(
        distinctUntilChanged(),
        tap(ctrl => {
          ctrl.setValidators([Validators.required, Validators.pattern(this.patternRegex)]);
          ctrl.setAsyncValidators([this.g4AccountExistsValidator()]);
          ctrl.updateValueAndValidity();
        }),

        takeUntilDestroyed()
      )
      .subscribe();
  }

  private g4AccountExistsValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }
      return of(control.value).pipe(
        delay(300),
        switchMap(value => this.service.getG4BusinessAccount(value)),
        map(result => {
          if (result) {
            this.accountDetails.set(result);
            return null;
          }
          this.accountDetails.set(null);
          return { accountDoesNotExist: true };
        }),
        catchError(() => {
          this.accountDetails.set(null);
          return of({ accountDoesNotExist: true });
        })
      );
    };
  }
}
